/**
 * `/nagrania` — every coached client's recordings, newest first, and the clients beside them (R27 of
 * `exercise-clip-timeline-requirements.md`; variant A of `docs/design/nagrania-klientow-2026-09-24/`).
 *
 * The read is paged by a cursor, so the wall is every page that has landed, in the order the backend
 * wrote them; the months are drawn over the whole of it and a month cut by a page boundary is one
 * month. The client list and every count come from the first page's `clients`, which is the whole
 * collection whatever the wall is narrowed to — handed in separately, because the caller may still
 * hold the list from an earlier read when this one has none.
 */

import {
  clipMonths,
  type ClipMonth,
  type ClipPerson,
  type CollectionClient,
  type CollectionPage,
} from '~/entities/exercise-clip';
import { filmedSetWords } from '~/entities/lift-history';
import { calendarDaysSince } from '~/shared/lib/calendarDate';
import { dateTimeFormat, relativeTimeFormat } from '~/shared/lib/intlCache';
import type { Translate } from '~/shared/lib/localeTranslate';

export interface RecordingsClientRow {
  readonly customerId: number;
  readonly person: ClipPerson;
  readonly unread: boolean;
  /** „Ostatnie 22 wrz, 10 nagrań". */
  readonly meta: string;
  readonly count: number;
  /** „10 nagrań": the header's line while this client narrows the wall. */
  readonly countWords: string;
}

export interface RecordingsView {
  readonly months: readonly ClipMonth[];
  /** Whose card a tile opens on, by the clip's id. */
  readonly clientOfClip: ReadonlyMap<number, number>;
  readonly clients: readonly RecordingsClientRow[];
  /** Every recording in the collection. */
  readonly total: number;
  /** „6 osób z nagraniami", under „Wszyscy klienci". */
  readonly people: string;
  /** „24 nagrania od 6 klientów, od najnowszych", under the screen's title. */
  readonly summary: string;
  /** The client the wall is narrowed to, where the list knows them. */
  readonly selected: RecordingsClientRow | null;
}

export interface RecordingsInput {
  readonly pages: readonly CollectionPage[];
  /** The whole collection's clients, or `null` while no read has named them. */
  readonly clients: readonly CollectionClient[] | null;
  /** The client the wall is narrowed to, or `null` for every client. */
  readonly customerId: number | null;
  readonly viewerUserId: string | null;
}

export interface RecordingsDeps {
  readonly t: Translate;
  readonly locale: string;
  readonly now: Date;
}

/** „dzisiaj", „wczoraj", then „22 wrz" — when a client last filmed, as the list says it. */
function dayWords(at: number, { locale, now }: RecordingsDeps): string {
  const days = calendarDaysSince(new Date(at), now);
  if (days === 0 || days === 1) return relativeTimeFormat(locale, { numeric: 'auto' }).format(-days, 'day');
  return dateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(at));
}

export function buildRecordings(input: RecordingsInput, deps: RecordingsDeps): RecordingsView {
  const { t, locale } = deps;
  const clips = input.pages.flatMap((page) => page.clips);
  const clients = (input.clients ?? []).map((client): RecordingsClientRow => {
    const countWords = t('web:recordings.clips', { count: client.clipCount });
    return {
      customerId: client.customerId,
      person: client.person,
      unread: client.hasUnread,
      meta: t('web:recordings.clientMeta', { day: dayWords(client.newestPerformedOn, deps), count: countWords }),
      count: client.clipCount,
      countWords,
    };
  });
  const total = clients.reduce((sum, client) => sum + client.count, 0);
  const byId = new Map(clients.map((client) => [client.customerId, client.person]));

  return {
    months: clipMonths(
      clips.map((clip) => ({
        clip,
        exerciseName: clip.exerciseName,
        setWords: filmedSetWords(clip.anchor, undefined, { t, locale }),
        client: input.customerId === null ? (byId.get(clip.customerId) ?? null) : null,
      })),
      { t, locale, viewerUserId: input.viewerUserId, author: false },
    ),
    clientOfClip: new Map(clips.map((clip) => [clip.id, clip.customerId])),
    clients,
    total,
    people: t('web:recordings.people', { count: clients.length }),
    summary: t('web:recordings.summary', {
      clips: t('web:recordings.clips', { count: total }),
      clients: t('web:recordings.fromClients', { count: clients.length }),
    }),
    selected: clients.find((client) => client.customerId === input.customerId) ?? null,
  };
}

/** `?klient=<id>`: the one parameter `/nagrania` understands. */
export interface RecordingsSearch {
  readonly klient?: number;
}

/** A positive integer or nothing; an unreadable `?klient=` is dropped rather than kept as noise. */
export function validateRecordingsSearch(raw: Record<string, unknown>): RecordingsSearch {
  const klient = Number(raw.klient);
  return Number.isInteger(klient) && klient > 0 ? { klient } : {};
}
